import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import ContextCarryOver from "@/components/ContextCarryOver";
import { RATINGS } from "@/components/blueprints/FiveForcesBlueprint";

const STEPS = [
  { n: 1, code: "1", key: "entrants", label: "New Entrants", phase: "STRATEGY", hint: "Barriers to entry, economies of scale, initial capital requirements." },
  { n: 2, code: "2", key: "buyers", label: "Buyer Power", phase: "STRATEGY", hint: "Customer leverage, volume dependencies, switching costs." },
  { n: 3, code: "3", key: "suppliers", label: "Supplier Power", phase: "STRATEGY", hint: "Vendor concentration, unique material controls, input scarcity." },
  { n: 4, code: "4", key: "substitutes", label: "Substitutes", phase: "STRATEGY", hint: "Alternative solutions, workarounds, shifting consumer habits." },
  { n: 5, code: "5", key: "rivalry", label: "Rivalry", phase: "STRATEGY", hint: "Competitor density, fixed-cost exit barriers, market stagnation." },
];
const LAST = STEPS.length;
const taCls = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";

export default function FiveForces() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [data, setData] = useState({});
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/documents/${id}`); setTitle(res.data.title || ""); setData(res.data.payload || {}); }
      catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id]); // eslint-disable-line

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled Five Forces Analysis", payload: data, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, data]);

  const cur = STEPS[step - 1];
  const force = data[cur.key] || { rating: "", notes: "" };
  const setForce = (patch) => setData((d) => ({ ...d, [cur.key]: { ...force, ...patch } }));
  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => { if (await persist({ status: "Completed" })) { toast.success("Analysis finalised"); navigate(`/forces/${id}/view`); } };
  const applySuggestion = (text) => { setForce({ notes: !force.notes?.trim() ? text : `${force.notes.trimEnd()}\n- ${text}` }); toast.success("Suggestion added"); };
  const onPull = ({ title: t, context }) => { if (context) setData((d) => ({ ...d, entrants: { ...(d.entrants || {}), notes: context } })); if (!title && t) setTitle(t); toast.success("Context pulled in"); };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="forces-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Five Forces analysis title…" className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />
        <div className="mt-5 grid grid-cols-5 gap-2" data-testid="forces-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)} className={`rounded-sm border px-2 py-2.5 text-center transition-all ${step === s.n ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-xs font-bold block ${step === s.n ? "text-indigo-700" : "text-slate-400"}`}>F{s.n}</span>
              <p className={`text-[11px] font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>
        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <h2 className="text-xl font-semibold text-slate-900">{cur.label}</h2>
          <p className="text-sm text-slate-500">{cur.hint}</p>
          {step === 1 && <ContextCarryOver currentId={id} onPull={onPull} />}
          <div>
            <label className="block text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1.5">Pressure Rating</label>
            <div className="inline-flex rounded-sm bg-slate-100 p-1" data-testid="forces-rating">
              {RATINGS.map((r) => (
                <button key={r} data-testid={`rating-${r.toLowerCase()}`} onClick={() => setForce({ rating: r })} className={`px-4 py-1.5 rounded-sm text-sm font-medium transition-all ${force.rating === r ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{r}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1.5">Analysis Notes</label>
            <textarea data-testid={`field-${cur.key}`} rows={6} className={taCls} value={force.notes || ""} onChange={(e) => setForce({ notes: e.target.value })} placeholder="Document the evidence behind this rating…" />
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="forces-back" onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3">
            <button data-testid="forces-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
            {step < LAST ? (
              <button data-testid="forces-next" onClick={next} className="inline-flex items-center gap-2 rounded-sm bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700">Next Force <ArrowRight size={16} /></button>
            ) : (
              <button data-testid="forces-finalise" onClick={finalise} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"><CheckCircle size={18} weight="fill" /> Finalise Analysis</button>
            )}
          </div>
        </div>
      </div>
      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase={cur.phase} />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="five_forces" step={cur} phase={cur.phase} onApplySuggestion={applySuggestion} />
    </Layout>
  );
}
